import {
  AlignLeft,
  BookOpenText,
  ExternalLink,
  FileText,
  ListTree,
  Pilcrow,
  Ruler,
  SlidersHorizontal,
  TextQuote,
  Type,
} from "lucide-react";

import { EmphasisRulesEditor } from "./EmphasisRulesEditor";
import type {
  ManuscriptTemplate,
  ParagraphStyleRule,
} from "../domain/templates";

type FormatRequirementsEditorProps = {
  template: ManuscriptTemplate;
  onChange: (template: ManuscriptTemplate) => void;
  onOpenFullDesigner: () => void;
};

type NumberInputProps = {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (value: number) => void;
};

function NumberInput({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: NumberInputProps) {
  return (
    <label className="format-number-field">
      <span className="field-label">{label}</span>
      <span>
        <input
          aria-label={label}
          max={max}
          min={min}
          name={label}
          autoComplete="off"
          onChange={(event) => onChange(Number(event.target.value))}
          step={step}
          type="number"
          value={value}
        />
        <small>{unit}</small>
      </span>
    </label>
  );
}

function ParagraphRuleCard({
  label,
  description,
  rule,
  onChange,
}: {
  label: string;
  description: string;
  rule: ParagraphStyleRule;
  onChange: (patch: Partial<ParagraphStyleRule>) => void;
}) {
  return (
    <fieldset className="paragraph-rule-card">
      <legend>
        <strong>{label}</strong>
        <small>{description}</small>
      </legend>
      <div className="paragraph-rule-fields">
        <NumberInput
          label={`${label}行距`}
          max={3}
          min={1}
          onChange={(lineSpacing) => onChange({ lineSpacing })}
          step={0.1}
          unit="倍"
          value={rule.lineSpacing}
        />
        <NumberInput
          label={`${label}首行缩进`}
          max={20}
          min={0}
          onChange={(firstLineIndentMm) => onChange({ firstLineIndentMm })}
          step={0.1}
          unit="mm"
          value={rule.firstLineIndentMm}
        />
        <NumberInput
          label={`${label}段前间距`}
          max={36}
          min={0}
          onChange={(spaceBeforePt) => onChange({ spaceBeforePt })}
          step={0.5}
          unit="pt"
          value={rule.spaceBeforePt}
        />
        <NumberInput
          label={`${label}段后间距`}
          max={36}
          min={0}
          onChange={(spaceAfterPt) => onChange({ spaceAfterPt })}
          step={0.5}
          unit="pt"
          value={rule.spaceAfterPt}
        />
        <label className="format-select-field paragraph-rule-alignment">
          <span className="field-label">{label}对齐</span>
          <select
            aria-label={`${label}对齐`}
            onChange={(event) =>
              onChange({
                alignment: event.target.value as ParagraphStyleRule["alignment"],
              })
            }
            value={rule.alignment}
          >
            <option value="left">左对齐</option>
            <option value="center">居中</option>
            <option value="right">右对齐</option>
            <option value="justify">两端对齐</option>
          </select>
        </label>
      </div>
    </fieldset>
  );
}

export function FormatRequirementsEditor({
  template,
  onChange,
  onOpenFullDesigner,
}: FormatRequirementsEditorProps) {
  const updateParagraph = (patch: Partial<ManuscriptTemplate["paragraph"]>) =>
    onChange({
      ...template,
      paragraph: { ...template.paragraph, ...patch },
    });
  const updateReferences = (patch: Partial<ManuscriptTemplate["references"]>) =>
    onChange({
      ...template,
      references: { ...template.references, ...patch },
    });
  const updatePage = (patch: Partial<ManuscriptTemplate["page"]>) =>
    onChange({
      ...template,
      page: { ...template.page, ...patch },
    });
  const updateTypography = (
    patch: Partial<ManuscriptTemplate["typography"]>,
  ) =>
    onChange({
      ...template,
      typography: { ...template.typography, ...patch },
    });
  const updateEmphasisRules = (
    emphasisRules: ManuscriptTemplate["emphasisRules"],
  ) => onChange({ ...template, emphasisRules });
  const updateMargins = (
    patch: Partial<ManuscriptTemplate["page"]["marginsMm"]>,
  ) =>
    updatePage({
      marginsMm: { ...template.page.marginsMm, ...patch },
    });
  const updateBlockStyle = (
    key: keyof ManuscriptTemplate["blockStyles"],
    patch: Partial<ParagraphStyleRule>,
  ) =>
    onChange({
      ...template,
      blockStyles: {
        ...template.blockStyles,
        [key]: { ...template.blockStyles[key], ...patch },
      },
    });

  return (
    <main className="editor-panel">
      <div className="editor-hero">
        <div className="hero-icon">
          <SlidersHorizontal size={22} />
        </div>
        <div>
          <span className="eyebrow">FORMAT REQUIREMENTS</span>
          <h1>字体、页面、正文与参考文献格式</h1>
          <p>每类段落、字号、页边距、页码和行号都会同步到预览与 Word。</p>
        </div>
      </div>

      <section className="editor-card format-requirements-card">
        <div className="format-source-note">
          <AlignLeft size={17} />
          <p>
            {template.id === "sci-conservative" ? (
              <>
                当前基线采用双倍行距、页码和连续行号，符合 Nature 与 PLOS ONE
                等常见送审要求；它仍不是统一 SCI 标准。
              </>
            ) : (
              <>
                不存在所有 SCI 期刊通用的首行缩进值。当前设置属于
                <strong>{template.name}</strong>。
              </>
            )}
            最终应以目标期刊或学校的最新官方模板为准。
          </p>
        </div>

        <div className="format-rule-section">
          <div className="format-rule-heading">
            <Type size={18} />
            <span>
              <strong>中英文字体与字号</strong>
              <small>中文和英文分别设置；所有导出文字仍强制为黑色。</small>
            </span>
          </div>
          <div className="format-fields-grid format-font-grid">
            <label className="format-text-field">
              <span className="field-label">英文字体</span>
              <input
                aria-label="英文字体"
                autoComplete="off"
                name="latin-font"
                onChange={(event) =>
                  updateTypography({ latinFont: event.target.value })
                }
                value={template.typography.latinFont}
              />
            </label>
            <label className="format-text-field">
              <span className="field-label">中文字体</span>
              <input
                aria-label="中文字体"
                autoComplete="off"
                name="east-asia-font"
                onChange={(event) =>
                  updateTypography({ eastAsiaFont: event.target.value })
                }
                value={template.typography.eastAsiaFont}
              />
            </label>
            <NumberInput
              label="正文字号"
              max={18}
              min={8}
              onChange={(bodySizePt) => updateTypography({ bodySizePt })}
              step={0.5}
              unit="pt"
              value={template.typography.bodySizePt}
            />
            <NumberInput
              label="论文题名字号"
              max={32}
              min={12}
              onChange={(titleSizePt) => updateTypography({ titleSizePt })}
              step={0.5}
              unit="pt"
              value={template.typography.titleSizePt}
            />
            <NumberInput
              label="一级标题字号"
              max={24}
              min={9}
              onChange={(h1SizePt) => updateTypography({ h1SizePt })}
              step={0.5}
              unit="pt"
              value={template.typography.h1SizePt}
            />
            <NumberInput
              label="二级标题字号"
              max={22}
              min={8}
              onChange={(h2SizePt) => updateTypography({ h2SizePt })}
              step={0.5}
              unit="pt"
              value={template.typography.h2SizePt}
            />
            <NumberInput
              label="三级标题字号"
              max={20}
              min={8}
              onChange={(h3SizePt) => updateTypography({ h3SizePt })}
              step={0.5}
              unit="pt"
              value={template.typography.h3SizePt}
            />
            <NumberInput
              label="表题/表注字号"
              max={16}
              min={8}
              onChange={(captionSizePt) => updateTypography({ captionSizePt })}
              step={0.5}
              unit="pt"
              value={template.typography.captionSizePt}
            />
            <NumberInput
              label="图注字号"
              max={16}
              min={8}
              onChange={(figureCaptionSizePt) =>
                updateTypography({ figureCaptionSizePt })
              }
              step={0.5}
              unit="pt"
              value={template.typography.figureCaptionSizePt}
            />
          </div>
        </div>

        <div className="format-rule-section emphasis-format-section">
          <div className="format-rule-heading">
            <TextQuote size={18} />
            <span>
              <strong>文字强调规则</strong>
              <small>填入稿件中需要自动加粗、斜体或同时加粗并斜体的原文。</small>
            </span>
          </div>
          <EmphasisRulesEditor
            onChange={updateEmphasisRules}
            rules={template.emphasisRules}
          />
        </div>

        <div className="format-rule-section">
          <div className="format-rule-heading">
            <Ruler size={18} />
            <span>
              <strong>页面与页边距</strong>
              <small>不同期刊可选择 A4 或 Letter，并分别调整四边页边距。</small>
            </span>
          </div>
          <div className="format-fields-grid">
            <label className="format-select-field">
              <span className="field-label">纸张大小</span>
              <select
                aria-label="纸张大小"
                onChange={(event) =>
                  updatePage({ size: event.target.value as "A4" | "Letter" })
                }
                value={template.page.size}
              >
                <option value="A4">A4</option>
                <option value="Letter">Letter</option>
              </select>
            </label>
            {(
              [
                ["top", "上边距"],
                ["right", "右边距"],
                ["bottom", "下边距"],
                ["left", "左边距"],
              ] as const
            ).map(([side, label]) => (
              <NumberInput
                key={side}
                label={label}
                max={60}
                min={5}
                onChange={(value) => updateMargins({ [side]: value })}
                step={0.1}
                unit="mm"
                value={template.page.marginsMm[side]}
              />
            ))}
          </div>
        </div>

        <div className="format-rule-section">
          <div className="format-rule-heading">
            <FileText size={18} />
            <span>
              <strong>送审辅助标记</strong>
              <small>PLOS ONE 要求整篇连续行号；其他期刊可关闭或改为逐页、逐节重启。</small>
            </span>
          </div>
          <div className="format-toggle-grid">
            <label className="format-check-option">
              <input
                aria-label="页码"
                checked={template.page.pageNumbers}
                onChange={(event) => updatePage({ pageNumbers: event.target.checked })}
                type="checkbox"
              />
              <span>
                <strong>页码</strong>
                <small>便于审稿意见定位</small>
              </span>
            </label>
            <label className="format-check-option">
              <input
                aria-label="行号"
                checked={template.page.lineNumbers}
                onChange={(event) => updatePage({ lineNumbers: event.target.checked })}
                type="checkbox"
              />
              <span>
                <strong>行号</strong>
                <small>开启后可设置重启方式</small>
              </span>
            </label>
          </div>
          {template.page.lineNumbers ? (
            <div className="format-fields-grid line-number-settings">
              <label className="format-select-field">
                <span className="field-label">行号方式</span>
                <select
                  aria-label="行号方式"
                  onChange={(event) =>
                    updatePage({
                      lineNumberMode: event.target
                        .value as ManuscriptTemplate["page"]["lineNumberMode"],
                    })
                  }
                  value={template.page.lineNumberMode}
                >
                  <option value="continuous">整篇连续</option>
                  <option value="restartPage">每页重新开始</option>
                  <option value="restartSection">每节重新开始</option>
                </select>
              </label>
              <NumberInput
                label="行号起始值"
                max={9999}
                min={1}
                onChange={(lineNumberStart) => updatePage({ lineNumberStart })}
                step={1}
                unit=""
                value={template.page.lineNumberStart}
              />
              <NumberInput
                label="行号显示间隔"
                max={20}
                min={1}
                onChange={(lineNumberInterval) =>
                  updatePage({ lineNumberInterval })
                }
                step={1}
                unit="行"
                value={template.page.lineNumberInterval}
              />
            </div>
          ) : null}
        </div>

        <div className="format-rule-section">
          <div className="format-rule-heading">
            <Pilcrow size={18} />
            <span>
              <strong>正文段落</strong>
              <small>不作用于题名、作者、摘要标签和标题。</small>
            </span>
          </div>
          <div className="format-fields-grid">
            <NumberInput
              label="正文首行缩进"
              max={20}
              min={0}
              onChange={(firstLineIndentMm) => updateParagraph({ firstLineIndentMm })}
              step={0.1}
              unit="mm"
              value={template.paragraph.firstLineIndentMm}
            />
            <NumberInput
              label="正文行距"
              max={3}
              min={1}
              onChange={(lineSpacing) => updateParagraph({ lineSpacing })}
              step={0.1}
              unit="倍"
              value={template.paragraph.lineSpacing}
            />
            <NumberInput
              label="正文段前间距"
              max={36}
              min={0}
              onChange={(spaceBeforePt) => updateParagraph({ spaceBeforePt })}
              step={0.5}
              unit="pt"
              value={template.paragraph.spaceBeforePt}
            />
            <NumberInput
              label="正文段后间距"
              max={36}
              min={0}
              onChange={(spaceAfterPt) => updateParagraph({ spaceAfterPt })}
              step={0.5}
              unit="pt"
              value={template.paragraph.spaceAfterPt}
            />
            <label className="format-select-field">
              <span className="field-label">正文对齐</span>
              <select
                aria-label="正文对齐"
                onChange={(event) =>
                  updateParagraph({
                    alignment: event.target.value as "left" | "justify",
                  })
                }
                value={template.paragraph.alignment}
              >
                <option value="left">左对齐</option>
                <option value="justify">两端对齐</option>
              </select>
            </label>
          </div>
        </div>

        <div className="format-rule-section">
          <div className="format-rule-heading">
            <ListTree size={18} />
            <span>
              <strong>摘要与标题段落</strong>
              <small>摘要、H1、H2、H3 分别设置；标题正文仍使用“正文段落”规则。</small>
            </span>
          </div>
          <div className="paragraph-rule-list">
            <ParagraphRuleCard
              description="摘要正文与关键词"
              label="摘要"
              onChange={(patch) => updateBlockStyle("abstract", patch)}
              rule={template.blockStyles.abstract}
            />
            <ParagraphRuleCard
              description="Introduction、Results 等一级标题"
              label="一级标题"
              onChange={(patch) => updateBlockStyle("h1", patch)}
              rule={template.blockStyles.h1}
            />
            <ParagraphRuleCard
              description="一级标题下的二级标题"
              label="二级标题"
              onChange={(patch) => updateBlockStyle("h2", patch)}
              rule={template.blockStyles.h2}
            />
            <ParagraphRuleCard
              description="二级标题下的三级标题"
              label="三级标题"
              onChange={(patch) => updateBlockStyle("h3", patch)}
              rule={template.blockStyles.h3}
            />
            <ParagraphRuleCard
              description="Figure 1 等独立图注段落"
              label="图注"
              onChange={(patch) => updateBlockStyle("figureCaption", patch)}
              rule={template.blockStyles.figureCaption}
            />
          </div>
        </div>

        <div className="format-rule-section">
          <div className="format-rule-heading">
            <BookOpenText size={18} />
            <span>
              <strong>参考文献段落</strong>
              <small>默认保留用户输入内容；仅在模板要求时自动重排编号。</small>
            </span>
          </div>
          <div className="format-fields-grid">
            <label className="format-select-field">
              <span className="field-label">参考文献编号</span>
              <select
                aria-label="参考文献编号"
                onChange={(event) =>
                  updateReferences({
                    numbering: event.target.value as "preserve" | "numeric",
                  })
                }
                value={template.references.numbering}
              >
                <option value="preserve">保留原文</option>
                <option value="numeric">按当前顺序生成 1, 2, 3</option>
              </select>
            </label>
            <NumberInput
              label="参考文献悬挂缩进"
              max={20}
              min={0}
              onChange={(hangingIndentMm) => updateReferences({ hangingIndentMm })}
              step={0.1}
              unit="mm"
              value={template.references.hangingIndentMm}
            />
            <NumberInput
              label="参考文献行距"
              max={3}
              min={1}
              onChange={(lineSpacing) => updateReferences({ lineSpacing })}
              step={0.1}
              unit="倍"
              value={template.references.lineSpacing}
            />
            <NumberInput
              label="参考文献段后间距"
              max={36}
              min={0}
              onChange={(spaceAfterPt) => updateReferences({ spaceAfterPt })}
              step={0.5}
              unit="pt"
              value={template.references.spaceAfterPt}
            />
          </div>
        </div>

        <button className="open-full-designer" onClick={onOpenFullDesigner} type="button">
          <ExternalLink size={15} /> 打开完整模板设计器（模板身份与全部参数）
        </button>
      </section>
    </main>
  );
}
